import React, { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Separator } from "@/components/ui/separator";
import { X, Filter } from "lucide-react";
import { cn } from "@/lib/utils";

export type FilterField = "status" | "event" | "name" | "email" | "phone" | "userId" | "payment" | "price" | "quantity";
export type FilterOperator = "contains" | "equals" | ">=" | "<=" | ">" | "<";

export interface FilterTag {
  id: string;
  field: FilterField;
  value: string;
  label: string;
  operator?: FilterOperator;
}

interface ColumnFiltersProps {
  filterTags: FilterTag[];
  onAddFilter: (field: FilterField, value: string, operator?: FilterOperator) => void;
  onRemoveFilter: (id: string) => void;
  onClearAllFilters: () => void;
}

const FIELD_OPTIONS: { 
  value: FilterField; 
  label: string; 
  placeholder: string; 
  options?: string[];
  operators?: FilterOperator[];
}[] = [
  { value: "status", label: "Status", placeholder: "Select status", options: ["confirmed", "pending", "refunded"] },
  { value: "event", label: "Event", placeholder: "Event name...", operators: ["contains", "equals"] },
  { value: "name", label: "Guest Name", placeholder: "Guest name...", operators: ["contains", "equals"] },
  { value: "email", label: "Email", placeholder: "Email address...", operators: ["contains", "equals"] },
  { value: "phone", label: "Phone", placeholder: "Phone number...", operators: ["contains", "equals"] },
  { value: "userId", label: "User ID", placeholder: "User ID...", operators: ["contains", "equals"] },
  { value: "payment", label: "Payment", placeholder: "Select payment", options: ["stripe", "credits", "unlimited", "free"] },
  { value: "price", label: "Price", placeholder: "Enter amount...", operators: ["equals", ">=", "<=", ">", "<"] },
  { value: "quantity", label: "Quantity", placeholder: "Enter count...", operators: ["equals", ">=", "<=", ">", "<"] },
];

const FIELD_COLORS: Record<FilterField, string> = {
  status: "bg-emerald-500/10 text-emerald-700 border-emerald-200",
  event: "bg-blue-500/10 text-blue-700 border-blue-200",
  name: "bg-green-500/10 text-green-700 border-green-200",
  email: "bg-purple-500/10 text-purple-700 border-purple-200",
  phone: "bg-orange-500/10 text-orange-700 border-orange-200",
  userId: "bg-slate-500/10 text-slate-700 border-slate-200",
  payment: "bg-amber-500/10 text-amber-700 border-amber-200",
  price: "bg-yellow-500/10 text-yellow-700 border-yellow-200",
  quantity: "bg-cyan-500/10 text-cyan-700 border-cyan-200",
};

const OPERATOR_LABELS: Record<FilterOperator, string> = {
  contains: "contains",
  equals: "=",
  ">=": "≥",
  "<=": "≤",
  ">": ">",
  "<": "<",
};

const ColumnFilters: React.FC<ColumnFiltersProps> = ({
  filterTags,
  onAddFilter,
  onRemoveFilter,
  onClearAllFilters,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [selectedField, setSelectedField] = useState<FilterField>("status");
  const [selectedOperator, setSelectedOperator] = useState<FilterOperator>("contains");
  const [inputValue, setInputValue] = useState("");

  const currentFieldConfig = FIELD_OPTIONS.find(f => f.value === selectedField);
  const hasOptions = currentFieldConfig?.options && currentFieldConfig.options.length > 0;
  const hasOperators = !!currentFieldConfig?.operators && currentFieldConfig.operators.length > 0;

  const handleAddFilter = () => {
    if (!inputValue.trim()) return;
    const operator = hasOperators ? selectedOperator : undefined;
    onAddFilter(selectedField, inputValue.trim(), operator);
    setInputValue("");
    setIsOpen(false);
  };

  const handleSelectOption = (value: string) => {
    onAddFilter(selectedField, value);
    setInputValue("");
    setIsOpen(false);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter") {
      e.preventDefault();
      handleAddFilter();
    }
  };

  const handleFieldChange = (field: FilterField) => {
    setSelectedField(field);
    setInputValue("");
    const fieldConfig = FIELD_OPTIONS.find((f) => f.value === field);
    if (fieldConfig?.operators?.length) {
      setSelectedOperator(fieldConfig.operators[0]);
    }
  };

  const formatTagDisplay = (tag: FilterTag) => {
    if (tag.operator) {
      return `${OPERATOR_LABELS[tag.operator]} ${tag.value}`;
    }
    return tag.value;
  };

  return (
    <div className="flex flex-wrap items-center gap-2">
      {/* Search button with popover */}
      <Popover open={isOpen} onOpenChange={setIsOpen}>
        <PopoverTrigger asChild>
          <Button variant="outline" size="sm" className="h-8 gap-1.5">
            <Filter className="h-3.5 w-3.5" />
            Filter
            {filterTags.length > 0 && (
              <span className="ml-1 rounded-full bg-primary/10 px-1.5 py-0.5 text-xs font-medium text-primary">
                {filterTags.length}
              </span>
            )}
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-80 p-3" align="end">
          <div className="space-y-3">
            <div className="space-y-1.5">
              <span className="text-xs font-medium text-muted-foreground">Field</span>
              <div className="flex flex-wrap gap-1">
                {FIELD_OPTIONS.map((field) => (
                  <Button
                    key={field.value}
                    variant={selectedField === field.value ? "default" : "outline"}
                    size="sm"
                    className="h-7 text-xs"
                    onClick={() => handleFieldChange(field.value)}
                  >
                    {field.label}
                  </Button>
                ))}
              </div>
            </div>

            <Separator />

            {hasOperators && (
              <div className="space-y-1.5">
                <span className="text-xs font-medium text-muted-foreground">Operator</span>
                <div className="flex flex-wrap gap-1">
                  {currentFieldConfig?.operators?.map((op) => (
                    <Button
                      key={op}
                      variant={selectedOperator === op ? "default" : "outline"}
                      size="sm"
                      className="h-7 text-xs min-w-[40px]"
                      onClick={() => setSelectedOperator(op)}
                    >
                      {OPERATOR_LABELS[op]}
                    </Button>
                  ))}
                </div>
              </div>
            )}

            <div className="space-y-1.5">
              <span className="text-xs font-medium text-muted-foreground">Value</span>
              {hasOptions ? (
                <div className="flex flex-wrap gap-1.5">
                  {currentFieldConfig.options!.map((opt) => (
                    <Button
                      key={opt}
                      variant="outline"
                      size="sm"
                      className="h-7 text-xs capitalize"
                      onClick={() => handleSelectOption(opt)}
                    >
                      {opt}
                    </Button>
                  ))}
                </div>
              ) : (
                <div className="flex gap-2">
                  <Input
                    value={inputValue}
                    onChange={(e) => setInputValue(e.target.value)}
                    onKeyDown={handleKeyDown}
                    placeholder={currentFieldConfig?.placeholder}
                    className="h-8 text-sm"
                    type={["price", "quantity"].includes(selectedField) ? "number" : "text"}
                  />
                  <Button size="sm" className="h-8" onClick={handleAddFilter}>
                    Add
                  </Button>
                </div>
              )}
            </div>
          </div>
        </PopoverContent>
      </Popover>

      {/* Filter tags */}
      {filterTags.map((tag) => (
        <div
          key={tag.id}
          className={cn(
            "flex items-center gap-1 h-7 px-2 rounded-md border text-xs font-medium",
            FIELD_COLORS[tag.field]
          )}
        >
          <span className="opacity-70">{tag.label}:</span>
          <span className="capitalize">{formatTagDisplay(tag)}</span>
          <button
            onClick={() => onRemoveFilter(tag.id)}
            className="ml-0.5 hover:opacity-70 transition-opacity"
          >
            <X className="h-3 w-3" />
          </button>
        </div>
      ))}

      {/* Clear all button */}
      {filterTags.length > 0 && (
        <Button
          variant="ghost"
          size="sm"
          className="h-7 px-2 text-xs text-muted-foreground hover:text-foreground"
          onClick={onClearAllFilters}
        >
          Clear all
        </Button>
      )}
    </div>
  );
};

export default ColumnFilters;
